import React from 'react';
import {connect} from 'react-redux';
import debug from 'debug';
import PropTypes from 'prop-types';
import Checkbox from '../Checkbox';
import NumberInput from '../NumberInput';
import Button from '../Button';
import RadioSet from '../RadioSet';
import {Dropdown} from '../../index';
import {_openModal} from '../../reducers/modals';

const log = debug('circleblack:preferences:core');

const formatCoreName = (core, panel) => {
  switch (core.title) {
    case 'overrideTPPAlerts':
      return core.displayName.replace('{{level}}', panel.displayName.split(' ')[0]);
    default:
      return core.displayName;
  }
};

const getCoreValues = (core) => {
  let value;
  let defaultValue;

  if (core.val instanceof Object) {
    value = core.val.value != null ? core.val.value : core.val.value; // eslint-disable-line eqeqeq
    defaultValue = core.val.defaultValue;
  } else {
    value = core.val != null ? core.val : core.value; // eslint-disable-line eqeqeq
    defaultValue = core.defaultValue;
  }

  return value != null ? value : defaultValue; // eslint-disable-line eqeqeq
};

// TODO Reqs pref core styling
function PrefCore({core, updateCore, group, panel, preferenceButtons = {}, preferenceCheckboxes = {}, session}) {
  log('rendering core', core.title);
  const disabled = core.disabled
    || (group.masterPref && group.masterPrefID !== core.id && !group.masterPref.val)
    || (group.enablingPref && !group.enablingPref.val);
  const value = getCoreValues(core);
  let defaultOption;
  let flatOptions;

  core.displayName = React.useMemo(() => formatCoreName(core, panel), []);

  const style = core.col !== 12 ? {
    minWidth: core.col * 75 + 'px',
    maxWidth: core.col * 100 + 'px',
  } : {};
  switch (core.dataType) {
    case 'checkbox':
    case 'entitlement':
      // TODO Reqs Help Text on checkbox as core.description, title if needed is core.displayName
      return <Checkbox width={style.minWidth}
                       title={core.displayName}
                       disabled={disabled || !!session.runAsInvestor}
                       dataPoint={{name: core.title, value: !!value}}
                       onChange={preferenceCheckboxes[core.title]
                         ? (name, val) => preferenceCheckboxes[core.title]({core, value: val, updateCore})
                         : updateCore}/>;
    case 'percent':
      return <NumberInput type={core.dataType}
                          dataPoint={{name: core.title, value: !window.isNaN(value) ? value / 100 : value}}
                          disabled={disabled || !!session.runAsInvestor}
                          title={core.displayName}
                          width={style.minWidth}
                          placeholder={core.val.defaultValue}
                          onBlur={(name, v) => updateCore(name, v * 100)}
                          validation={core.pattern ? {
                            custom: e => RegExp(core.pattern).test(e.value * 100 + '') ? '' : 'Invalid Entry',
                          } : undefined}/>;
    case 'number':
      return <NumberInput type={core.dataType}
                          dataPoint={{name: core.title, value}}
                          disabled={disabled || !!session.runAsInvestor}
                          title={core.displayName}
                          width={style.minWidth}
                          placeholder={core.val.defaultValue}
                          onBlur={updateCore}
                          validation={core.pattern ? {
                            custom: e => RegExp(core.pattern).test(e.value) ? '' : 'Invalid Entry',
                          } : undefined}/>;
    case 'radio':
      return <RadioSet dataPoint={{name: core.title, value}}
                       onChange={updateCore}
                       title={core.displayName}
                       disabled={core.disabled || !!session.runAsInvestor}
                       options={core.items.map(item => ({
                         label: item.displayName,
                         value: item.val,
                       }))}/>;
    case 'button':
      return <Button label={core.displayName}
                     importance={'secondary'}
                     size={'sm'}
                     disabled={disabled || !!session.runAsInvestor}
                     action={() => preferenceButtons[core.title](core, panel.cat, panel.target)}/>;
    case 'dropdown':
      flatOptions = core.options.reduce((flat, option) => option.options ? flat.concat(option.options) : flat.concat(option), []);
      defaultOption = flatOptions.find(option => option.id === core.defaultValue);

      // ETODO figure out expected prop values for dropdowns (e.g. clearable, placeholder)
      // ETODO what if no default option? need reqs
      return <Dropdown title={core.displayName}
                       placeholder={'Select...'}
                       type={core.dataType}
                       dataPoint={{
                         name: core.title,
                         value: value
                           ? {label: flatOptions.find(option => option.id === value).displayName, value}
                           : {label: defaultOption.displayName, value: defaultOption.id},
                       }}
                       options={core.options.map(option => ({
                         label: option.displayName,
                         ...(option.id !== undefined && {value: option.id} || {}),
                         ...(option.options && {
                           options: option.options.map(inner => ({
                             label: inner.displayName,
                             value: inner.id,
                           })),
                         }),
                       }))}
                       defaultValue={(defaultOption && {
                         label: defaultOption.displayName,
                         value: defaultOption.id,
                       })}
                       onChange={(title, val) => updateCore(title, val.value)}
                       disabled={!!session.runAsInvestor}/>;
    default:
      return core.displayName + ' - Unsupported Data Type or ' + core.dataType;
  }
}

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modal => dispatch(_openModal(modal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PrefCore);

PrefCore.propTypes = {
  /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number,
  }).isRequired,
  core: PropTypes.object.isRequired,
  /**
   * Function used to update the core with a new value, passed into children
   */
  updateCore: PropTypes.func.isRequired,
  /**
   * Optional custom functions to be used based on core title for buttons.
   */
  preferenceButtons: PropTypes.object,
  /**
   * Optional custom functions to be used based on core title for checkboxes.
   */
  preferenceCheckboxes: PropTypes.object,
  group: PropTypes.shape({
    masterPref: PropTypes.shape({
      val: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    }),
    masterPrefID: PropTypes.number,
    enablingPref: PropTypes.shape({
      val: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    }),
    enablingPrefID: PropTypes.number,
  }),
  panel: PropTypes.shape({
    /**
     * Display Name for the panel
     */
    displayName: PropTypes.string.isRequired,
    /**
     * Passed to preferenceButtons functions
     */
    cat: PropTypes.object,
    /**
     * Passed to preferenceButtons functions
     */
    target: PropTypes.object,
  }),
};
